// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { formatCategories } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";

type CartLineData = Pick<
    CartLineModel,
    "shortDescription" | "brand" | "erpNumber" | "pricing" | "qtyOrdered" | "properties"
>;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const pageType = getCurrentPage(state).type
    const isPLP = pageType === "ProductListPage";
    const isPDP = pageType === "ProductDetailsPage";
    const isSearchPage = state.pages.productList.isSearchPage;

    const list = isSearchPage
        ? "Search Result Page"
        : isPLP
        ? "Product List Page"
        : isPDP
        ? "Product Detail Page"
        : null;

    if (state.xGoogleAnalytics.addToCartEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else {
            cartLinesToAdd = [props.apiResult];
        }

        const session = getSession(state);
        const currentBillTo = getCurrentBillToState(state).value;
        const isAuthenticated = xIsAuthenticated(state);

        const googleAnalyticsData = {
            event: "add_to_cart",
            currency: session.currency?.currencyCode,
            value: props.apiResult.pricing?.extendedUnitNetPrice || 0,
            affiliation: currentBillTo?.companyName || "",
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            branch_number: (session as any)?.defaultWarehouse?.name || "",
            branch_name: (session as any)?.defaultWarehouse?.description || "",
            sales_rep: (session as any).salesperson?.name || "",
            items: cartLinesToAdd.map((cartLine, index) => ({
                item_id: cartLine.erpNumber,
                item_name: cartLine.shortDescription || "",
                coupon: "",
                affiliation: currentBillTo?.companyName || "",
                discount: cartLine.pricing
                    ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                        ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                        : 0
                    : 0,
                index,
                item_brand: cartLine.brand?.name || "",
                ...formatCategories(cartLine.properties?.category),
                item_list_id: pageType,
                item_list_name: list,
                item_variant: "",
                location_id: (session as any)?.pickUpWarehouse?.id || "",
                price: cartLine.pricing?.unitNetPrice || 0,
                quantity: cartLine.qtyOrdered,
            })),
        };

        dataLayer.push(googleAnalyticsData);
    }
};

const _GaAddProductToCart = XngGaAddProductToCart as any;
addToEndOfChain(AddToCartChain, _GaAddProductToCart);
addToEndOfChain(AddWishListToCartChain, _GaAddProductToCart);
addToEndOfChain(AddCartLinesChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, _GaAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, _GaAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, _GaAddProductToCart);
